/* eslint-disable @typescript-eslint/naming-convention */
import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {
  AccessType,
  CHAT_MESSAGES_LIMIT,
  ConnectionStatus,
  ReminderEmailStatus
} from '@app/modules/shared/data/constants';
import {Message, MessageType} from '@app/models/message';
import {Store} from '@ngxs/store';
import {LocalUser, LocalUserState} from '@app/state/local-user.state';
import {ChatService} from '@app/modules/chat/services/chat.service';
import {RemoteParticipantInfo} from '@app/models/remote-participant-info';
import {RemoteUserAction} from '@app/state/remote-users.state';
import {Whiteboard, WhiteboardState} from '@app/state/whiteboard.state';
import {UtilService} from '@app/modules/shared/services/util.service';
import {CoreService} from '@app/services/core.service';
import {AssignmentState, AssignmentStateModel} from '@app/state/assignment.state';
import {ClassroomState, ClassroomStateModel} from '@app/state/classroom-state.service';

@Component({
  selector: 'app-user-list-item',
  templateUrl: './user-list-item.component.html',
  styleUrls: ['./user-list-item.component.css']
})
export class UserListItemComponent implements OnInit {
  @Input()
  public remoteUser: RemoteParticipantInfo;
  @Input()
  public index: number;
  @Input()
  public studentsCount: number;
  @ViewChild('inputValue')
  public inputMassage; // accessing the reference element

  public localUser: LocalUser;
  public whiteboard: Whiteboard;
  public sendReminderEmailStatus: ReminderEmailStatus = ReminderEmailStatus.NOT_TRIGGERED;

  public MessageTypes = MessageType;
  public ReminderEmailStatus = ReminderEmailStatus;
  public RoomConnectionState = ConnectionStatus;
  public AccessType = AccessType;
  public UtilService = UtilService;
  public assignment: AssignmentStateModel;
  public isChatLimitExceeded: boolean = false;

  constructor(
    public utilService: UtilService,
    private chatService: ChatService,
    private coreService: CoreService,
    private store: Store
  ) {
    this.listenStoreChanges();
  }

  public ngOnInit(): void {}

  private listenStoreChanges(): void {
    this.store.select(LocalUserState).subscribe((localUser: LocalUser) => {
      this.localUser = localUser;
    });
    this.store.select(WhiteboardState).subscribe((whiteboard: Whiteboard) => {
      this.whiteboard = whiteboard;
    });
    this.store.select(AssignmentState).subscribe((assignment: AssignmentStateModel) => {
      this.assignment = assignment;
    });
    this.store.select(ClassroomState).subscribe((classroom: ClassroomStateModel) => {
      this.countStudentsMessages();
    });
  }

  public expelObserver(): void {
    this.coreService.sendExpelStaff(this.remoteUser.id).subscribe();
  }

  public countStudentsMessages(){
    setTimeout(()=> {
      if (this.remoteUser.accessType !== AccessType.STUDENT){
        return;
      }

      const messagesLength: number = this.remoteUser.additionalInfo.messages
        .filter((message: Message): boolean => message.messageType === MessageType.TO_STAFF)
        .reduce((result: number, currentMessage: Message): number => {
          return result + currentMessage.text.length;
        }, 0);
      this.isChatLimitExceeded = messagesLength >= CHAT_MESSAGES_LIMIT;
    });
  }

  // public cleanAllMessages(): void {
  //   // TODO - implement
  // }

  public toggleWhiteboardSharing(): void {
    this.coreService.toggleWhiteboardsSharingWith(this.remoteUser.id);
  }

  public toggleAssignmentSwitchAccessWith(): void {
    this.coreService.toggleAssignmentSwitchAccessWith(this.remoteUser.id);
  }

  public clearIndividualChat(): void {
    this.chatService.sendClearUserMessages(this.remoteUser.id).subscribe();
  }

  public manuallyHideUnansweredMessageIcon(): void {
    if (this.localUser.accessType === AccessType.OBSERVER) {
      return;
    }

    this.store.dispatch(new RemoteUserAction.SetHasUnansweredMessage(this.remoteUser.id, false));

    const messageModel = new Message();
    messageModel.senderId = this.localUser.id;
    messageModel.targetId = this.remoteUser.id;
    messageModel.messageType = MessageType.SEEN;
    this.chatService.sendMessage(messageModel).subscribe();
    this.chatService.sendClassWideMessage(messageModel).subscribe();
  }

  public sendMessage(message: string): void {
    if (message.length > 0) {
      const messageModel = new Message();
      messageModel.senderId = this.localUser.id;
      messageModel.targetId = this.remoteUser.id;
      messageModel.text = message;
      messageModel.messageType = this.getMessageType(this.remoteUser);
      this.chatService.sendMessage(messageModel).subscribe();
      this.chatService.sendClassWideMessage(messageModel).subscribe();
      this.inputMassage.nativeElement.value = '';
    }
  }

  public correctAnswerScore(): void {
    let count: number = this.remoteUser.additionalInfo.correctAnswersCount;
    this.chatService.sendScore(this.remoteUser.id, ++count).subscribe(() => {
      this.store.dispatch(new RemoteUserAction.SetCorrectAnswer(this.remoteUser.id, count));
    });
    const messageModel = new Message();
    messageModel.senderId = this.localUser.id;
    messageModel.targetId = this.remoteUser.id;
    messageModel.text = 'Correct';
    messageModel.messageType = MessageType.TO_STUDENT;

    this.chatService.sendMessage(messageModel).subscribe();
    this.chatService.sendClassWideMessage(messageModel).subscribe();
  }

  public incorrectAnswerScore(): void {
    const messageModel = new Message();
    messageModel.senderId = this.localUser.id;
    messageModel.targetId = this.remoteUser.id;
    messageModel.text = 'Incorrect';
    messageModel.messageType = MessageType.TO_STUDENT;

    this.chatService.sendMessage(messageModel).subscribe();
    this.chatService.sendClassWideMessage(messageModel).subscribe();
  }

  public resetStudentScores(): void {
    this.chatService.sendResetScore(this.remoteUser.id).subscribe(() => {
      this.remoteUser.additionalInfo.correctAnswersCount = 0;
    });
  }

  public sendEmailToAbsentStudent(): void {
    this.sendReminderEmailStatus = ReminderEmailStatus.PENDING;
    this.coreService.sendRemindMessages(this.remoteUser.id, this.remoteUser.name)
      .subscribe(() => this.sendReminderEmailStatus = ReminderEmailStatus.SUCCESS,() =>  this.sendReminderEmailStatus = ReminderEmailStatus.ERROR);
  }

  public toggleShowMessages(): void {
    this.store.dispatch(new RemoteUserAction.SetCollapseMessagesBox(this.remoteUser.id, !this.remoteUser.additionalInfo.isMessageBoxCollapsed));
  }

  private getMessageType(user: RemoteParticipantInfo): number {
    switch (user.accessType) {
      case AccessType.SUPPORT:
      case AccessType.OBSERVER: {
        return MessageType.TO_STAFF;
      }
      case AccessType.STUDENT: {
        return MessageType.TO_STUDENT;
      }
    }
  }
}
